import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { map } from 'rxjs/operators';
import {
    CreateExtendedAttributeSuccess,
    DeleteExtendedAttribute,
    DeselectSelectedExtendedAttributes,
    FetchAllAdminExtendedAttributes,
    FetchAllAdminExtendedAttributesByModuleType,
    FetchAllAdminExtendedAttributesByModuleTypeFailure,
    FetchAllAdminExtendedAttributesByModuleTypeSuccess,
    FetchAllAdminExtendedAttributesFailure,
    FetchAllAdminExtendedAttributesSuccess,
    FetchAllExtendedAttributes,
    FetchAllExtendedAttributesFailure,
    FetchAllExtendedAttributesSuccess,
    SearchExtendedAttributes,
    SelectFilteredExtendedAttributes,
    SetExtendedAttributes,
    ToggleSelectedExtendedAttribute,
} from '../actions';
import { AddExtendedAttributes } from '../actions/add-entities.action';
import { ExtendedAttributeApiService } from '../services/extended-attribute.api-service';
import { ExtendedAttribute } from './extended-attribute';
import { ExtendedAttributeState } from './extended-attribute.state';
import { ExtendedAttributesStateModel } from './extended-attributes.state-model';

@State<ExtendedAttributesStateModel>({
    name: 'extendedAttributes',
    defaults: new ExtendedAttributesStateModel(),
})
@Injectable()
export class ExtendedAttributesState extends SubentityState<ExtendedAttribute> {
    @Selector([ExtendedAttributeState.getEntities])
    static getFilteredEntities(state: ExtendedAttributesStateModel, extendedAttributes: ExtendedAttribute[]): ExtendedAttribute[] {
        if (!extendedAttributes) {
            return null;
        }

        return extendedAttributes
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, ExtendedAttribute], b: [number, ExtendedAttribute]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, ExtendedAttribute]) => x[1]);
    }

    constructor(private store: Store, private _extendedAttributeApiService: ExtendedAttributeApiService) {
        super(null, 0);
    }

    @Action(AddExtendedAttributes)
    addEntities(ctx: StateContext<ExtendedAttributesStateModel>, action: AddExtendedAttributes): void {
        const extendedAttributes = this.store.selectSnapshot(ExtendedAttributeState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: action.payload.map((entity) => entity.id) } }, extendedAttributes);
    }

    @Action(CreateExtendedAttributeSuccess)
    createEntity(ctx: StateContext<ExtendedAttributesStateModel>, action: CreateExtendedAttributeSuccess): void {
        const extendedAttributes = this.store.selectSnapshot(ExtendedAttributeState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: [action.payload.id] } }, extendedAttributes);
    }

    @Action(FetchAllAdminExtendedAttributes)
    fetchAll(ctx: StateContext<ExtendedAttributesStateModel>, action: FetchAllAdminExtendedAttributes): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetExtendedAttributes,
            FetchAllAdminExtendedAttributesSuccess,
            FetchAllAdminExtendedAttributesFailure,
            this._extendedAttributeApiService.findAllAdminAttributes(action.payload.categories, action.payload.enabledForFilter).pipe(
                map((res) => {
                    ctx.patchState({
                        totalCount: res.pagination.number_of_results,
                    });

                    return res.data.map((query) => new ExtendedAttribute(query));
                })
            )
        );
    }

    @Action(FetchAllAdminExtendedAttributesByModuleType)
    fetchAllAdminExtendedAttributesByModuleType(ctx: StateContext<ExtendedAttributesStateModel>, action: FetchAllAdminExtendedAttributesByModuleType): void {
        this._extendedAttributeApiService.findAllAdminAttributes([action.payload.category], action.payload.enabledForFilter).subscribe(
            (res) => {
                ctx.dispatch(new FetchAllAdminExtendedAttributesByModuleTypeSuccess(res.data.map((query) => new ExtendedAttribute(query))));
            },
            (error) => {
                ctx.dispatch(new FetchAllAdminExtendedAttributesByModuleTypeFailure());
            }
        );
    }

    @Action(FetchAllAdminExtendedAttributesSuccess)
    fetchAllSuccess(ctx: StateContext<ExtendedAttributesStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchAllAdminExtendedAttributesFailure)
    fetchAllFailure(ctx: StateContext<ExtendedAttributesStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(FetchAllExtendedAttributes)
    fetchAllExtendedAttributes(ctx: StateContext<ExtendedAttributesStateModel>, action: FetchAllExtendedAttributes): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetExtendedAttributes,
            FetchAllExtendedAttributesSuccess,
            FetchAllExtendedAttributesFailure,
            this._extendedAttributeApiService.fetchAllExtendedAttributes(action.payload.categories, action.payload.enabledForFilter).pipe(
                map((res) => {
                    ctx.patchState({
                        totalCount: res.pagination.number_of_results,
                    });

                    return res.data.map((query) => new ExtendedAttribute(query));
                })
            )
        );
    }

    @Action(FetchAllExtendedAttributesSuccess)
    fetchAllAdminExtendedAttributesSuccess(ctx: StateContext<ExtendedAttributesStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchAllExtendedAttributesFailure)
    fetchAllAdminExtendedAttributesFailure(ctx: StateContext<ExtendedAttributesStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(DeleteExtendedAttribute)
    deleteEntity({ getState, patchState }: StateContext<ExtendedAttributesStateModel>, { payload }: DeleteExtendedAttribute): void {
        const state = getState();

        patchState({
            list: [...state.list.filter((subentityId) => subentityId !== payload)],
            selected: [...state.selected.filter((subentityId) => subentityId !== payload)],
            filtered: [...state.filtered.filter((subentityId) => subentityId !== payload)],
        });
    }

    @Action(SetExtendedAttributes)
    setEntities(ctx: StateContext<ExtendedAttributesStateModel>, action: SetExtendedAttributes): void {
        this._setSubentities(ctx, action, null);
    }

    @Action(ToggleSelectedExtendedAttribute)
    toggleSelect(ctx: StateContext<ExtendedAttributesStateModel>, action: ToggleSelectedExtendedAttribute): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredExtendedAttributes)
    selectAll(ctx: StateContext<ExtendedAttributesStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedExtendedAttributes)
    deselectAll(ctx: StateContext<ExtendedAttributesStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchExtendedAttributes)
    search(ctx: StateContext<ExtendedAttributesStateModel>, action: SearchExtendedAttributes): void {
        const extendedAttributes = this.store.selectSnapshot(ExtendedAttributeState.getEntities);

        this._searchSubentities(ctx, action, extendedAttributes);
    }
}
